import { createApiPath } from '@/shared/api/endpoints';
import { apiClient } from '@/shared/api/http-client';

/**
 * 웹 거래처 API (`/web/business-relationships/**`) 응답 타입 — 필드명은 서버 그대로 둔다 (docs/api/accounts.md)
 * 날짜는 "yyyy-MM-dd", 년월은 "yyyyMM" 문자열. 경로 키는 relationshipId (clinicId 아님).
 * 없는 · 남의 거래처는 404 RESOURCE_NOT_FOUND.
 */

/** 거래 상태별 건수 — 앱 TradeStatusCounts와 같은 키 */
export interface TradeStatusCounts {
  ongoing: number;
  applying: number;
  closed: number;
}

/** GET / — 좌측 목록 카드 */
export interface ClinicRelationship {
  relationshipId: number;
  clinicId: number;
  clinicName: string;
  clinicTypeCode: string | null;
  /** 의원급 | 병원급 */
  clinicTypeLabel: string;
  /** 심평원 종별명. 없으면 null */
  clinicTypeCodeName: string | null;
  address: string | null;
  phoneNumber: string | null;
  /** 요양기관번호 */
  ykiho: string | null;
  /** 대표 상태 (거래 중 · 거래처 승인 · 거래 신청 · 확인 중 · 이관 확인 중 · 거래 종료 · 신청 거절) */
  statusView: string;
  /** 거래 종료일 — 폐업으로 끝난 거래처는 null일 수 있다 */
  endedAt: string | null;
  clinicClosedOn: string | null;
  latestApprovalDate: string | null;
  lastConfirmedAmount: number | null;
  /** 지난 달 · 이번 달 정산월 (yyyyMM) */
  lastMonth: string;
  thisMonth: string;
  /** 그 정산월에 올린 이미지 장수 */
  lastMonthPrescriptionCount: number;
  thisMonthPrescriptionCount: number;
}

/** 서버 정렬 그대로(종료 · 거절은 뒤로). counts는 좌측 그룹 숫자 */
export interface ClinicRelationshipList {
  items: ClinicRelationship[];
  counts: TradeStatusCounts;
}

export interface PharmacyInfo {
  pharmacyId: number;
  pharmacyName: string;
  address: string | null;
  contactNumber: string | null;
  /** 폐업일. 값이 있으면 「정보 없음」으로 표시한다 (M-21) */
  closedOn: string | null;
}

/** PHARMA_COMPANY = 의원급(제약사 단위) · DRUG = 병원급(의약품 단위) */
export type TradeUnit = 'PHARMA_COMPANY' | 'DRUG';

/** 거래 행 상태 원값 */
export type TransactionStatusLiteral =
  | 'APPLIED'
  | 'RESUBMITTED'
  | 'IN_REVIEW'
  | 'PENDING_TRANSFER'
  | 'APPROVED'
  | 'REJECTED'
  | 'TERMINATED';

/**
 * 거래 행 (상세 trades · 드로어 공통).
 * 폐업으로 끝난 거래처 · 병원급에서 제약사 거래가 종료 · 거절된 의약품은
 * transactionStatus가 APPROVED여도 statusView "거래 불가"로 closed 섹션에 온다.
 */
export interface TradeRowResponse {
  /** 행 고유 키 (형식에 의존하지 않는다) */
  tradeId: string;
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  /** 병원급만. RU 코드는 "-" (M-12로 열지 않는다) */
  drugCode: string | null;
  drugName: string | null;
  transactionStatus: TransactionStatusLiteral | string;
  /** 거래중 · 신청중 · 확인 중 · 이관 확인 중 · 거래 불가 */
  statusView: string;
  /** 제약사 거래 신청 · 승인 · 종료일 */
  applicationDate: string | null;
  approvalDate: string | null;
  terminatedAt: string | null;
  /** 병원급 의약품 신청 · 승인일 (의원급 null) */
  drugApplicationDate: string | null;
  drugApprovalDate: string | null;
  minPrescriptionAmount: number | null;
}

export interface TradeSections {
  ongoing: TradeRowResponse[];
  applying: TradeRowResponse[];
  closed: TradeRowResponse[];
}

/** GET /{relationshipId} — 헤더 · 거래 섹션 · 미거래 요약 */
export interface BusinessRelationshipDetail {
  relationshipId: number;
  clinicId: number;
  clinicName: string;
  clinicTypeCode: string | null;
  clinicTypeLabel: string;
  clinicTypeCodeName: string | null;
  ykiho: string | null;
  departments: string[];
  address: string | null;
  phoneNumber: string | null;
  statusView: string;
  endedAt: string | null;
  clinicClosedOn: string | null;
  /** 내 사업자등록증 presigned URL — 만료되면 상세를 다시 부른다. 없으면 null */
  businessLicenseImageUrl: string | null;
  pharmacies: PharmacyInfo[];
  tradeUnit: TradeUnit;
  /** 거래 정보 카드 숫자 (= 드로어 totalCounts) */
  tradeStatusCounts: TradeStatusCounts;
  trades: TradeSections;
  /** 놓치고 있는 의약품 기준 처방월. 없으면 null */
  standardYearMonth: string | null;
  /** 신청 가능 의약품 수 (= M-22 applicableCount) */
  untradedApplicableCount: number;
}

/** 드로어 정렬 — 생략하면 의원급 승인일 · 병원급 신청일 최신순. 날짜 없는 행은 맨 뒤 */
export type TradeSort =
  'APPROVAL_DATE_DESC' | 'APPLICATION_DATE_DESC' | 'NAME_ASC';

/** GET /{relationshipId}/trades — M-19 · M-20 드로어. 세 섹션을 한 번에 준다 */
export interface TradeDrawerResponse extends TradeSections {
  tradeUnit: TradeUnit;
  /** 검색 전 전체 건수 */
  totalCounts: TradeStatusCounts;
  /** 검색 결과 수 */
  matchedCount: number;
}

/** 연도별 처방 등록 현황 행 — 이미지가 있는 (등록월, 정산월)만 온다 */
export interface PrescriptionRegistration {
  registrationYearMonth: string;
  settlementYearMonth: string;
  registrationStatus: 'REGISTERED' | string;
  /** pending + verified */
  imageCount: number;
  pendingImageCount: number;
  verifiedImageCount: number;
  /** 확정 · 게시 정산 있음 (금액 0도 true) */
  isSettlementConfirmed: boolean;
}

/** GET /{relationshipId}/prescription-registrations — 등록월↓ → 정산월↓ */
export interface PrescriptionRegistrations {
  registrationYear: number;
  items: PrescriptionRegistration[];
}

/** M-22 신청 불가 사유 */
export type UntradedDrugBlockReason =
  | 'SETTLEMENT_UNAVAILABLE'
  | 'OUT_OF_STOCK'
  | 'FILTERING_DISABLED'
  | 'NO_PHARMA_COMPANY'
  | 'ALREADY_REGISTERED'
  | 'NON_TRADABLE_DRUG';

export interface UntradedDrug {
  /** RU 코드는 "-" */
  drugCode: string;
  drugName: string;
  totalOutHospitalQuantity: number;
  price: number | null;
  pharmaCompanyName: string | null;
  pharmaCompanyId: number | null;
  /** 확정 당시 상태 — 판단에 쓰지 않는다 */
  approvalStatus: string | null;
  /** 신청 가능 여부는 이것만 본다 */
  canApply: boolean;
  blockReasonCode: UntradedDrugBlockReason | null;
  blockReasonLabel: string | null;
}

/** GET /{relationshipId}/untraded-drugs */
export interface UntradedDrugPage {
  /** 처방이 없으면 null */
  standardYearMonth: string | null;
  /** 필터 · 페이지와 무관한 전체 건수 */
  applicableCount: number;
  nonApplicableCount: number;
  content: UntradedDrug[];
  pageNo: number;
  pageSize: number;
  isLast: boolean;
  /** 현재 canApply 필터 결과 수 */
  totalCount: number;
}

export interface UntradedDrugParams {
  standardYearMonth?: string;
  canApply?: boolean;
  page?: number;
  size?: number;
}

export interface TradeParams {
  keyword?: string;
  sort?: TradeSort;
}

const path = createApiPath.businessRelationships;

export const businessRelationshipApi = {
  list: async () => {
    const { data } = await apiClient.get<ClinicRelationshipList>(path.list());
    return data;
  },
  detail: async (relationshipId: number) => {
    const { data } = await apiClient.get<BusinessRelationshipDetail>(
      path.detail(relationshipId),
    );
    return data;
  },
  trades: async (relationshipId: number, params: TradeParams) => {
    const { data } = await apiClient.get<TradeDrawerResponse>(
      path.trades(relationshipId),
      { params: { keyword: params.keyword || undefined, sort: params.sort } },
    );
    return data;
  },
  prescriptionRegistrations: async (
    relationshipId: number,
    registrationYear: number,
  ) => {
    const { data } = await apiClient.get<PrescriptionRegistrations>(
      path.prescriptionRegistrations(relationshipId),
      { params: { registrationYear } },
    );
    return data;
  },
  untradedDrugs: async (relationshipId: number, params: UntradedDrugParams) => {
    const { data } = await apiClient.get<UntradedDrugPage>(
      path.untradedDrugs(relationshipId),
      { params },
    );
    return data;
  },
};
